import { RemoveRedEye, School } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';

import { Grid as GridLoader } from 'react-loader-spinner';
import { useDispatch } from 'react-redux';
import { default as ADC } from '../api/admin-department-category';
import CrapBulkToken from '../api/crapBulkToken';
import User from '../api/user';
import { show } from '../reducers/notificationSlice';
import ChipSelector from './ChipSelector';
import CrapBulkTokenManager from './CrapBulkTokenManager';
const STATUS = [
  { name: 'Approved', value: 'approved' },
  { name: 'Rejected', value: 'rejected' },
  { name: 'Deleted', value: 'deleted' },
  { name: 'Pending', value: 'pending' },
];
const STATUS_COLOR = {
  pending: { name: 'Pending', color: 'primary' },
  approved: { name: 'Approved', color: 'success' },
  deleted: { name: 'Deleted', color: 'error' },
  rejected: { name: 'Rejected', color: 'warning' },
};

function SelectMenu({ items, value, setValue }) {
  return (
    <Box sx={{ p: 1 }}>
      <ChipSelector items={items} value={value} setValue={setValue} size="small" color="warning" />
    </Box>
  );
}

function InstitutionActionButtons({
  onViewDetails,
  onShowSpecializations,
  onCreateBulkToken,
  onViewBulkTokens,
  compact = false,
}) {
  return (
    <Stack
      direction={compact ? { xs: 'column', sm: 'row' } : 'column'}
      spacing={1}
      useFlexGap
      sx={{ width: '100%', flexWrap: 'wrap' }}
    >
      <Button variant="contained" color="primary" onClick={onViewDetails}>
        View Details
      </Button>
      <Button variant="outlined" color="info" onClick={onShowSpecializations}>
        Specialisations
      </Button>
      <Button variant="contained" color="success" onClick={onCreateBulkToken}>
        Create Token
      </Button>
      <Button variant="outlined" color="secondary" onClick={onViewBulkTokens}>
        View Tokens
      </Button>
    </Stack>
  );
}

function InstitutionStatusEditor({
  currentStatus,
  draftStatus,
  setDraftStatus,
  onUpdate,
}) {
  return (
    <Box
      sx={{
        width: '100%',
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5 }}>
        Change status
      </Typography>
      <SelectMenu value={draftStatus || currentStatus} setValue={setDraftStatus} items={STATUS} />
      {draftStatus !== '' && draftStatus !== currentStatus ? (
        <Button variant="contained" color="warning" fullWidth onClick={onUpdate}>
          Apply Status Update
        </Button>
      ) : null}
    </Box>
  );
}

export default function Institution() {
  const dispatch = useDispatch();
  const [selectedInstitute, setSelectedInstitute] = useState({});
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState('college');
  const [status, setStatus] = useState('all');
  const STATUS = [
    {
      name: 'All',
      value: 'all',
      onClick: () => setStatus('all'),
    },
    {
      name: 'Pending',
      value: 'pending',
      onClick: () => setStatus('pending'),
    },
    {
      name: 'Approved',
      value: 'approved',
      onClick: () => setStatus('approved'),
    },
    {
      name: 'Deleted',
      value: 'deleted',
      onClick: () => setStatus('deleted'),
    },
    {
      name: 'Rejected',
      value: 'rejected',
      onClick: () => setStatus('rejected'),
    },
  ];

  useEffect(() => {
    User.getInstitutions(institution === 'college', status !== 'all' ? { status } : '')
      .then((response) => setData(response.data))
      .catch((err) =>
        dispatch(show({ message: err?.response?.data?.message, open: true, severity: 'error' }))
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution, status]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="College"
            variant={institution === 'college' ? 'contained' : 'outlined'}
            color="primary"
            className="cursor-pointer"
            onClick={() => setInstitution('college')}
          />
          <Chip
            label="University"
            variant={institution === 'university' ? 'contained' : 'outlined'}
            color="secondary"
            className="cursor-pointer"
            onClick={() => setInstitution('university')}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {STATUS.map((ele, idx) => (
            <Chip
              label={ele.name}
              key={idx}
              variant={status === ele.value ? 'contained' : 'outlined'}
              color="info"
              className="cursor-pointer"
              size="small"
              onClick={ele.onClick}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            alignItems: { xs: 'center', md: 'flex-start' },
            py: 2,
            width: '100%',
          }}
        >
          {data !== null ? (
            data?.length > 0 ? (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {data?.map((ele) => (
                  <InstitutionListRow
                    {...{ ...ele, setOpen, setSelectedInstitute }}
                    key={ele.id}
                    institutionKind={institution}
                  />
                ))}
              </Box>
            ) : (
              <Typography>No records found</Typography>
            )
          ) : (
            <div className="flex justify-center mt-20">
              <GridLoader />
            </div>
          )}
        </Box>
      </Box>
      <InstitutionDetails {...{ open, setOpen, selectedInstitute }} />
    </Box>
  );
}

function InstitutionListRow({
  id,
  name,
  email,
  profileTypeID,
  profileImageURL,
  status,
  institutionKind = 'institution',
  setOpen,
  setSelectedInstitute,
}) {
  const dispatch = useDispatch();
  const [openSpecialization, showSpecialization] = useState(false);
  const [openBulkToken, setOpenBulkToken] = useState(false);
  const [openBulkTokenList, setOpenBulkTokenList] = useState(false);
  const [_status, setStatus] = useState('');
  const [updatedStatus, setUpdatedStatus] = useState('');
  const [imageError, setImageError] = useState(false);
  const effectiveStatus = updatedStatus || status;

  const handleViewDetails = () => {
    setSelectedInstitute(() => ({ userID: id, profileTypeID }));
    setOpen(() => true);
  };

  const handleUpdateStatus = () => {
    User.updateInstitutionStatus(id, _status, profileTypeID)
      .then(() => {
        setUpdatedStatus(_status);
        dispatch(show({ message: 'Status updated successfully', open: true, severity: 'success' }));
      })
      .catch((err) =>
        dispatch(show({ message: err?.response?.data?.message, open: true, severity: 'error' }))
      );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', minWidth: 0 }}>
            <Avatar
              src={profileImageURL && !imageError ? profileImageURL : undefined}
              alt={name}
              onError={() => setImageError(true)}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                boxShadow: '0 4px 12px rgba(25,118,210,0.18)',
              }}
            >
              <School />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {email || 'No email available'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {institutionKind === 'college' ? 'College profile' : 'University profile'}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Chip
              label={STATUS_COLOR[effectiveStatus].name}
              color={STATUS_COLOR[effectiveStatus].color}
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Fab
              size="small"
              color="secondary"
              onClick={handleViewDetails}
              sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
            >
              <RemoveRedEye fontSize="small" />
            </Fab>
          </Stack>
        </Box>

        <InstitutionActionButtons
          compact
          onViewDetails={handleViewDetails}
          onShowSpecializations={() => showSpecialization(true)}
          onCreateBulkToken={() => setOpenBulkToken(true)}
          onViewBulkTokens={() => setOpenBulkTokenList(true)}
        />

        <InstitutionStatusEditor
          currentStatus={status}
          draftStatus={_status}
          setDraftStatus={setStatus}
          onUpdate={handleUpdateStatus}
        />
      </Stack>

      {openSpecialization && (
        <InstitutionSpecializationDetails
          id={id}
          open={openSpecialization}
          setOpen={showSpecialization}
        />
      )}
      <CrapBulkTokenManager
        institutionID={id}
        institutionName={name}
        open={openBulkToken}
        onClose={() => setOpenBulkToken(false)}
      />
      <InstitutionBulkTokenList
        institutionID={id}
        institutionName={name}
        open={openBulkTokenList}
        onClose={() => setOpenBulkTokenList(false)}
      />
    </Paper>
  );
}

function InstitutionBulkTokenList({ institutionID, institutionName, open, onClose }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [deactivatingToken, setDeactivatingToken] = useState('');

  const formatDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  };

  useEffect(() => {
    if (!open || !institutionID) return;

    let active = true;
    setLoading(true);
    setItems([]);

    CrapBulkToken.listTokens(institutionID)
      .then(({ data }) => {
        if (!active) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (!active) return;
        dispatch(show({ message: err?.response?.data?.message || 'Failed to load bulk tokens', open: true, severity: 'error' }));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dispatch, institutionID, open]);

  const handleDeactivateToken = async (tokenNumber) => {
    if (!tokenNumber || deactivatingToken) return;
    setDeactivatingToken(tokenNumber);
    try {
      await CrapBulkToken.deactivateToken(tokenNumber);
      setItems((prev) => prev.map((item) => (
        item.tokenNumber === tokenNumber
          ? { ...item, active: false }
          : item
      )));
      dispatch(show({ message: `Token ${tokenNumber} deactivated`, open: true, severity: 'success' }));
    } catch (err) {
      dispatch(show({ message: err?.response?.data?.message || 'Failed to deactivate token', open: true, severity: 'error' }));
    } finally {
      setDeactivatingToken('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Bulk Tokens
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {institutionName || institutionID}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
        {loading ? null : items.length === 0 ? (
          <Alert severity="info">No bulk tokens found for this college.</Alert>
        ) : (
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((item, index) => (
              <ListItem
                key={`${item.tokenNumber}-${index}`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <ListItemText
                    primary="Token Number"
                    secondary={item.tokenNumber || '—'}
                    secondaryTypographyProps={{
                      sx: {
                        mt: 0.5,
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        color: 'text.primary',
                      },
                    }}
                  />
                  <Chip
                    size="small"
                    color={item.active ? 'success' : 'default'}
                    label={item.active ? 'Active' : 'Inactive'}
                  />
                </Box>

                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={!item.active || deactivatingToken === item.tokenNumber}
                    onClick={() => handleDeactivateToken(item.tokenNumber)}
                  >
                    {deactivatingToken === item.tokenNumber ? 'Deactivating...' : 'Deactivate token'}
                  </Button>
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">Institution ID</Typography>
                    <Typography variant="body2">{item.institutionID || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Department ID</Typography>
                    <Typography variant="body2">{item.departmentID || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Specialisation ID</Typography>
                    <Typography variant="body2">{item.specialisationID || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Batch ID</Typography>
                    <Typography variant="body2">{item.batchID || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Batch Year</Typography>
                    <Typography variant="body2">{item.batchYear || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Batch Details</Typography>
                    <Typography variant="body2">{item.batchDetails || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Scope Type</Typography>
                    <Typography variant="body2">{item.scopeType || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatDateTime(item.created)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last Modified</Typography>
                    <Typography variant="body2">{formatDateTime(item.modified)}</Typography>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InstitutionDetails({ selectedInstitute, open, setOpen }) {
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedInstitute.userID) {
      setLoading(true);
      User.getUserDetails(selectedInstitute.userID)
        .then(({ data }) => {
          setData(data);
          setLoading(false);
        })
        .catch((err) => {
          dispatch(show({ message: err?.response?.data?.message, open: true, severity: 'error' }));
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitute.userID]);

  if (!selectedInstitute.userID) return <></>;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(() => false);
        setData(() => {});
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <School color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {data?.name || 'Institution Details'}
          </Typography>
        </Box>
        {data?.status && (
          <Chip
            label={STATUS_COLOR[data.status]?.name || data.status}
            color={STATUS_COLOR[data.status]?.color || 'default'}
            size="medium"
            sx={{ fontWeight: 600 }}
          />
        )}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <GridLoader />
          </Box>
        ) : data?.id ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Contact Information */}
            <Box className="flex gap-2 flex-col">
              <Typography variant="h6" className="underline text-blue-600" sx={{ mb: 1 }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {data.email && (
                  <Typography>
                    <strong>Email:</strong> {data.email}
                  </Typography>
                )}
                {data.phone && (
                  <Typography>
                    <strong>Phone:</strong> {data.phone}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* About */}
            {data.description && (
              <Box className="flex gap-2 flex-col">
                <Typography variant="h6" className="underline text-blue-600" sx={{ mb: 1 }}>
                  About
                </Typography>
                <Typography>{data.description}</Typography>
              </Box>
            )}

            {/* Address Details */}
            {(data.addressLineOne || data.addressLineTwo) && (
              <Box className="flex gap-2 flex-col">
                <Typography variant="h6" className="underline text-blue-600" sx={{ mb: 1 }}>
                  Address Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {data.addressLineOne && (
                    <Typography>
                      <strong>Address Line 1:</strong> {data.addressLineOne}
                    </Typography>
                  )}
                  {data.addressLineTwo && (
                    <Typography>
                      <strong>Address Line 2:</strong> {data.addressLineTwo}
                    </Typography>
                  )}
                  {data.stateID && (
                    <Typography>
                      <strong>State ID:</strong> {data.stateID}
                    </Typography>
                  )}
                  {data.countryID && (
                    <Typography>
                      <strong>Country ID:</strong> {data.countryID}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Additional Information */}
            <Box className="flex gap-2 flex-col">
              <Typography variant="h6" className="underline text-blue-600" sx={{ mb: 1 }}>
                Additional Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {data.institutionTypeID && (
                  <Typography>
                    <strong>Institution Type ID:</strong> {data.institutionTypeID}
                  </Typography>
                )}
                {data.profileTypeID !== undefined && (
                  <Typography>
                    <strong>Profile Type ID:</strong> {data.profileTypeID}
                  </Typography>
                )}
                {data.created && (
                  <Typography>
                    <strong>Created:</strong> {formatDate(data.created)}
                  </Typography>
                )}
                {data.modified && (
                  <Typography>
                    <strong>Last Modified:</strong> {formatDate(data.modified)}
                  </Typography>
                )}
                {data.id && (
                  <Typography>
                    <strong>Institution ID:</strong> {data.id}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ py: 4, textAlign: 'center' }}>No data available</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InstitutionSpecializationDetails({ id, open, setOpen }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [adminDepartmentCategory, setAdminDepartmentCategory] = useState([]);
  const [selectedAdminCategory, setSelectedAdminCategory] = useState({});
  useEffect(() => {
    ADC.getAdminDepartmentCategorySpecialization(id)
      .then(({ data }) => {
        setRows(data);
      })
      .catch((e) => {
        console.log(e);
      });
    ADC.getAdminDepartmentCategories()
      .then(({ data }) => {
        setAdminDepartmentCategory(data);
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle className="flex justify-center font-bold">Specialization List</DialogTitle>
      <DialogContent>
        {rows?.length > 0 ? (
          <Box className="md:w-[500px] min-w-full">
            {rows.map((ele) => (
              <div
                className="text-lg p-2 flex flex-col bg-slate-100 rounded-md my-2 border-zinc-400 border"
                key={ele.id}
              >
                {adminDepartmentCategory?.filter(
                  (e) => e.id === ele.department_admin_category_id
                )[0]?.name && (
                  <span className="bg-green-300 p-2 rounded-lg">
                    {
                      adminDepartmentCategory?.filter(
                        (e) => e.id === ele.department_admin_category_id
                      )[0]?.name
                    }
                  </span>
                )}
                <span>{ele?.degree_name}</span>
                <span>{ele?.dept_name}</span>
                <span>{ele?.spec_name}</span>
                <div className="flex flex-col self-start w-full gap-2">
                  <Typography variant="caption" className="text-gray-600">
                    Admin Category:
                  </Typography>
                  <ChipSelector
                    items={adminDepartmentCategory.map((e) => ({ name: e.name, value: e.id }))}
                    value={selectedAdminCategory[ele.spec_id] || ''}
                    setValue={(adminDepartmentId) =>
                      setSelectedAdminCategory((_o) => ({
                        ..._o,
                        [ele.spec_id]: adminDepartmentId,
                      }))
                    }
                    size="small"
                    color="primary"
                  />
                  <Button
                    color="warning"
                    variant="contained"
                    size="small"
                    onClick={() => {
                      ADC.addDepartmentAdminCategorySpecialization({
                        department_admin_category_id: selectedAdminCategory[ele.spec_id],
                        specialization_id: ele?.spec_id,
                      })
                        .then(({ data }) => {
                          dispatch(
                            show({
                              message: 'Specialization is added in Admin Category ',
                              open: true,
                              severity: 'success',
                            })
                          );
                        })
                        .catch((err) =>
                          dispatch(
                            show({
                              message: err.response.data.message,
                              open: true,
                              severity: 'error',
                            })
                          )
                        );
                    }}
                  >
                    Set Admin Category
                  </Button>
                </div>
              </div>
            ))}
          </Box>
        ) : (
          <div>No records found</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
